import {
  Box,
  Button,
  Flex,
  IconButton,
  Spinner,
  Step,
  StepIndicator,
  StepNumber,
  Stepper,
  StepSeparator,
  StepStatus,
  StepTitle,
  Text,
} from "@chakra-ui/react";
import ModalDrawer from "@components/ModalDrawer";
import { IoClose } from "react-icons/io5";
import { FaCheck } from "react-icons/fa6";

import { IoReloadOutline } from "react-icons/io5";

import BaseImage from "@components/BaseImage";
import { LSTIconMapping, TXN_TYPES } from "@constants/common";
import TransactionModal from "@components/TransactionModal";
import { TxnErrorType } from "@services/wagmi";
import { TransactionReceipt } from "viem";
import useDappChain from "@hooks/useDappChain";
import GainOpportunities from "./GainOpportunities";

export enum TransactionType {
  APPROVAL = "APPROVAL",
  DEPOSIT = "DEPOSIT",
}

interface DepositModalProps {
  transaction: {
    from: string;
    to: string;
    amountIn: string;
    amountOut: string;
  };
  complete: boolean;
  inProgress: boolean;
  type: TransactionType;
  error: {
    approval: TxnErrorType | null;
    deposit: TxnErrorType | null;
  };
  visible: boolean;
  status: TransactionType;
  flags: {
    isVault: boolean;
    isUnstake: boolean;
    isYieldVault?: boolean;
  };
  closeAlert: () => void;
  retryTransaction?: () => void;
  handleConfirm?: () => void;
  receipt: TransactionReceipt | null;
  nextAction?: () => void;
}
const TRANSACTIONS_KEYS = {
  [TXN_TYPES.VAULT_DEPOSIT]: "Deposit",
  [TXN_TYPES.YIELD_VAULT_DEPOSIT]: "Deposit",
  [TXN_TYPES.VAULT_WITHDRAW]: "Withdraw",
  [TXN_TYPES.YIELD_VAULT_WITHDRAW]: "Withdraw",
  [TXN_TYPES.STAKE]: "Restake",
  [TXN_TYPES.UNSTAKE]: "Unstake",
};

const DepositModal = (props: DepositModalProps) => {
  const { error, handleConfirm, receipt, flags, transaction, nextAction } =
    props;
  const { chain } = useDappChain();

  const transactionType = (() => {
    if (flags.isVault) {
      return flags.isUnstake
        ? flags.isYieldVault
          ? TXN_TYPES.YIELD_VAULT_WITHDRAW
          : TXN_TYPES.VAULT_WITHDRAW
        : flags.isYieldVault
        ? TXN_TYPES.YIELD_VAULT_DEPOSIT
        : TXN_TYPES.VAULT_DEPOSIT;
    }
    return flags.isUnstake ? TXN_TYPES.UNSTAKE : TXN_TYPES.STAKE;
  })();

  const showGainOpportunies =
    transactionType === TXN_TYPES.UNSTAKE && nextAction;
  const steps = [
    { title: `Approve ${transaction.from} in wallet` },
    { title: `Confirm ${TRANSACTIONS_KEYS[transactionType]}` },
  ];

  const closeModal = () => {
    props.closeAlert();
  };

  const onConfirm = () => {
    handleConfirm?.();
  };

  if (props.complete) {
    return (
      <TransactionModal
        isOpen={true}
        hash={receipt?.transactionHash || ""}
        error={error.deposit?.promptError || ""}
        transactionType={transactionType}
        isTxnProcessing={false}
        hasUserDenied={props.error.deposit?.hasUserDenied || false}
        closeAlert={() => closeModal()}
        handleTxView={() => {
          const url = `${chain.blockExplorers?.default.url}/tx/${receipt?.transactionHash}`;
          window.open(url, "_blank");
        }}
        chainId={chain.id}
        amount={transaction.amountOut}
      />
    );
  }

  return (
    <ModalDrawer
      isOpen={true}
      closeAlert={closeModal}
      closeOnOverlayClick={false}
      maxH={showGainOpportunies ? "95%" : "75%"}
      bodyProps={{ overflow: "hidden" }}
    >
      <Flex
        direction="column"
        gap={{ base: "1rem", md: "2rem" }}
        padding={{ base: "1rem 1rem 2.5rem", md: "1.5rem" }}
        width={{ base: "100%", md: "auto" }}
      >
        <Flex alignItems="center" justifyContent="space-between">
          <Text
            fontSize={{ base: "16px", md: "20px" }}
            lineHeight={{ base: "16px", md: "24px" }}
            fontWeight={600}
          >
            Review {TRANSACTIONS_KEYS[transactionType]}
          </Text>

          <IconButton
            icon={<IoClose size={24} color="#075A5A" />}
            aria-label="Close Icon"
            backgroundColor="transparent"
            _hover={{ backgroundColor: "transparent" }}
            onClick={closeModal}
          />
        </Flex>

        <Box padding="0.6rem 1rem" bg="#E4F8F8" borderRadius="1rem">
          <Flex
            justifyContent="space-between"
            gap={{ base: "2.5rem", md: "0rem" }}
            direction={{ base: "column", md: "row" }}
          >
            <Flex direction="column" gap="6px" justifyContent="center">
              <Text
                color="#3F6870"
                fontSize="12px"
                align={{ base: "left", md: "center" }}
                ml={{ base: "2.25rem", md: 0 }}
              >
                You pay
              </Text>
              <Flex gap="0.5rem" alignItems="center">
                <BaseImage
                  src={LSTIconMapping[props.transaction.from]}
                  width={32}
                  height={32}
                  alt={props.transaction.from}
                />
                <Text
                  fontSize="24px"
                  lineHeight="38px"
                  color="#075A5A"
                  fontWeight={500}
                >
                  {props.transaction.amountIn} {props.transaction.from}
                </Text>
              </Flex>
            </Flex>
            <Flex direction="column" gap="6px" justifyContent="center">
              <Text
                color="#3F6870"
                fontSize="12px"
                align={{ base: "left", md: "center" }}
                ml={{ base: "2.25rem", md: 0 }}
              >
                You get
              </Text>
              <Flex gap="0.5rem" alignItems="center">
                <BaseImage
                  src={LSTIconMapping[props.transaction.to]}
                  width={32}
                  height={32}
                  alt={props.transaction.to}
                />
                <Text
                  fontSize="24px"
                  lineHeight="38px"
                  color="#075A5A"
                  fontWeight={500}
                >
                  {props.transaction.amountOut} {props.transaction.to}
                </Text>
              </Flex>
            </Flex>
          </Flex>
        </Box>

        {/* KSP:214 - Gain Opportunities with footer buttons*/}
        {showGainOpportunies && (
          <GainOpportunities closeModal={closeModal} action={nextAction} />
        )}

        {!showGainOpportunies && props.type === TransactionType.APPROVAL && (
          <Stepper
            orientation="vertical"
            index={props.status === TransactionType.APPROVAL ? 0 : 1}
            height="120px"
            padding={{ base: "0 1rem", md: 0 }}
            gap={0}
            size="lg"
          >
            {steps.map((step, index) => {
              const showError = props.error.approval && index === 0;
              return (
                <Step key={index}>
                  <StepIndicator
                    background={showError ? "#FF4D4D" : "white"}
                    borderColor={showError ? "#FF4D4D !important" : "#B6DFDF"}
                  >
                    <StepStatus
                      incomplete={<StepNumber />}
                      active={
                        showError ? (
                          <IoClose size={20} color="white" />
                        ) : (
                          <Spinner size="sm" color="#0089D2" />
                        )
                      }
                      complete={<FaCheck />}
                    />
                  </StepIndicator>
                  <Box flexShrink="0" pt="0.35rem">
                    <StepTitle>
                      <Flex gap="0.75rem">
                        {step.title}
                        {showError && (
                          <Button
                            height="27px"
                            padding="6px 8px"
                            borderRadius="8px"
                            backgroundColor="#075A5A"
                            _hover={{
                              backgroundColor: "#075A5A",
                              color: "white",
                            }}
                            onClick={() => props?.retryTransaction?.()}
                            color="white"
                            leftIcon={
                              <IoReloadOutline width={12} height={12} />
                            }
                            iconSpacing="4px"
                            fontSize={10}
                            lineHeight={12}
                            fontWeight={400}
                          >
                            Retry
                          </Button>
                        )}
                      </Flex>
                    </StepTitle>
                  </Box>
                  <StepSeparator />
                </Step>
              );
            })}
          </Stepper>
        )}
        {!showGainOpportunies &&
          props.type === TransactionType.DEPOSIT &&
          (props.inProgress ? (
            <Flex gap="12px" alignItems="center">
              <Flex
                alignItems="center"
                justifyContent="center"
                border="1px solid #6E9BDE"
                borderRadius="50%"
                padding="8px"
              >
                <Spinner size="sm" color="#0089D2" />
              </Flex>
              <Text color="#075A5A">
                Confirming {TRANSACTIONS_KEYS[transactionType]}
              </Text>
            </Flex>
          ) : (
            <Button
              width="12rem"
              backgroundColor="#075A5A"
              color="white"
              _hover={{
                backgroundColor: "#075A5A",
                color: "white",
              }}
              alignSelf="center"
              borderRadius="0.5rem"
              onClick={onConfirm}
            >
              Confirm {TRANSACTIONS_KEYS[transactionType]}
            </Button>
          ))}
      </Flex>
    </ModalDrawer>
  );
};

export default DepositModal;
